import { useState, ReactNode, Children } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Chip, Heading, Text } from '@frontend/design-system';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    background: ${theme.colors.neutral5};
    gap: ${theme.spacing(3)};
    height: min-content;
    padding: ${theme.spacing(3)};

    @media (min-width: ${breakpoints.medium.min}px) {
      padding: ${theme.spacing(1)} 0 ${theme.spacing(3)};
    }
  `,
  cardContainer: css`
    display: flex;
    flex-direction: column;
    max-width: 275px;
    gap: ${theme.spacing(1.5)};
    padding: ${theme.spacing(3)};
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
  `,
};

export const StripePaymentStatistics = () => {
  const { t } = useTranslation('payments');
  return (
    <div css={styles.container} data-testid='stripe-payment-statistics'>
      <Heading level={2} textAlign='center'>
        {t("What You'll Achieve This Week")}
      </Heading>
      <StatisticsCarousel>
        <StatisticsCard
          title={t('Immediate Revenue')}
          message={t("Start accepting payments from patients you're already seeing")}
          statistic={t('$2,400 avg in first week')}
        />
        <StatisticsCard
          title={t('Save 2 Hours a Day')}
          message={t(
            '2+ hours saved each day by offices that use Text to Pay in their billing and collections process'
          )}
          statistic={t('83% customers get paid faster')}
        />
        <StatisticsCard
          title={t('Payment Plans')}
          message={t(
            '75% of uninsured adults under the age of 65 say they went without needed care because of the cost'
          )}
        />
      </StatisticsCarousel>
    </div>
  );
};

interface StatisticsCardProps {
  title: string;
  message: string;
  statistic?: string;
}

const StatisticsCard = ({ title, message, statistic }: StatisticsCardProps) => {
  return (
    <div css={styles.cardContainer}>
      <Heading level={3}>{title}</Heading>
      <Text>{message}</Text>
      {statistic && (
        <Chip variant='success' maxWidth='min-content' css={{ marginTop: 'auto' }}>
          {statistic}
        </Chip>
      )}
    </div>
  );
};

const carouselStyles = {
  container: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  content: css`
    display: flex;
    flex-wrap: nowrap;
    overflow: hidden;

    @media (min-width: ${breakpoints.medium.max}px) {
      gap: ${theme.spacing(3)};
      flex-direction: row;
      overflow: visible;
    }
  `,
  item: ({ index, activeIndex }: { index: number; activeIndex: number }) => css`
    display: ${index === activeIndex ? 'block' : 'none'};
    @media (min-width: ${breakpoints.medium.max}px) {
      display: flex;
    }
  `,
  button: css`
    @media (min-width: ${breakpoints.medium.max}px) {
      display: none;
    }
  `,
};

const StatisticsCarousel = ({ children }: { children: ReactNode }) => {
  const items = Children.toArray(children);
  const [activeIndex, setActiveIndex] = useState(0);

  const goToPrev = () => {
    setActiveIndex((prevIndex) => (prevIndex === 0 ? items.length - 1 : prevIndex - 1));
  };

  const goToNext = () => {
    setActiveIndex((prevIndex) => (prevIndex === items.length - 1 ? 0 : prevIndex + 1));
  };

  return (
    <div css={carouselStyles.container}>
      <Button css={carouselStyles.button} variant='secondary' iconName='caret-left' onClick={goToPrev} />
      <div css={carouselStyles.content}>
        {items.map((item, index) => (
          <div
            key={index}
            css={carouselStyles.item({ index, activeIndex })}
            className={`carousel-item ${index === activeIndex ? 'active' : ''}`}
          >
            {item}
          </div>
        ))}
      </div>
      <Button css={carouselStyles.button} variant='secondary' iconName='caret-right' onClick={goToNext} />
    </div>
  );
};
